import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import Grid from "../../../../../components/dialogs/Grid";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import {CancelButton} from "../../../../../components/button/CancelButton";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CollectionPermissionLevelDto} from "../../../../../common/apis/user/CollectionPermissionLevelDto";
import userService from "../../../../../common/apis/user/UserApiClient";
import userGroupApiClient from "../../../../../common/apis/usergroup/UserGroupApiClient";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {CommonTranslationKey} from "../../CommonTranslationKey";
import {UserGroupsTranslationKey} from "../UserGroupsTranslationKey";
import {invalidateFindAllUserGroupPermissionsQueryCache} from "../UserGroupQuery";
import {UserGroupCollectionPermissionDto} from "../../../../../common/apis/usergroup/UserGroupCollectionPermissionDto";

interface UserGroupPermissionUpdateDialogProps {
    open: boolean;
    groupId: string;
    permission?: UserGroupCollectionPermissionDto | null;
    onClose: () => void;
}

export default function UserGroupPermissionUpdateDialog(props: UserGroupPermissionUpdateDialogProps) {
    const {open, groupId, permission, onClose} = props;

    const queryClient = useQueryClient();

    const [permissionLevels, setPermissionLevels] = useState<CollectionPermissionLevelDto[]>([]);

    const [permissionLevel, setPermissionLevel] = useState<CollectionPermissionLevelDto | undefined>(permission?.permissionLevel);
    const [permissionLevelError, setPermissionLevelError] = useState<string | undefined>(undefined);

    useEffect(() => {
        userService.getAllPermissionLevels()
            .then(setPermissionLevels)
            .catch(() => setPermissionLevels([]));
    }, []);

    useEffect(() => {
        if (permission) {
            const level = permissionLevels.find((permissionLevel) => permissionLevel.code === permission.permissionLevel.code)
            setPermissionLevel(level ?? permission.permissionLevel);
        }
    }, [permission, permissionLevels]);

    function validatePermissionLevel(value: CollectionPermissionLevelDto | undefined | null): boolean {
        if (value) {
            setPermissionLevelError(undefined);
            return true;
        } else {
            setPermissionLevelError(t(CommonTranslationKey.FILL_OUT_THIS_FIELD));
            return false;
        }
    }

    function onSave() {
        if (!permission) {
            return;
        }
        if (validatePermissionLevel(permissionLevel)) {
            userGroupApiClient.createPermission(groupId, permission.collection.code, permissionLevel!.code)
                .then(() => {
                    invalidateFindAllUserGroupPermissionsQueryCache(groupId, queryClient);
                    onClose();
                    Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
                })
                .catch((e) => {
                    Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), e);
                });
        }
    }

    if (!permission) {
        return null;
    }

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle id="scroll-dialog-title"
                         title={t(UserGroupsTranslationKey.EDIT_PERMISSION)}
                         onDialogClosed={onClose}/>

            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <SingleSelectField
                            id="collection-field"
                            label={t(UserGroupsTranslationKey.PERMISSION_COLLECTION)}
                            value={permission.collection}
                            options={[permission.collection]}
                            getOptionLabel={(option) => option.name}
                            readOnly={true}/>
                    </Grid>
                    <Grid item xs={12}>
                        <SingleSelectField<CollectionPermissionLevelDto>
                            id="permission-level-field"
                            label={t(UserGroupsTranslationKey.PERMISSION_LEVEL)}
                            errorMessage={permissionLevelError}
                            value={permissionLevel ?? null}
                            onChange={(value) => setPermissionLevel(value ?? undefined)}
                            options={permissionLevels}
                            getOptionLabel={(option: CollectionPermissionLevelDto) => t(option.translationKey)}
                            required={true}/>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <CancelButton onClick={onClose}/>
                <Button color={"primary"} variant={"contained"} autoFocus onClick={onSave}>
                    {t(CommonTranslationKey.SAVE)}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
